// Pure helpers for the Incidents tab: labels, badge variants, durations,
// masking, sorting and the weekly customer 72-hour rows. No React, no network,
// so every rule here is covered by node-mode unit tests.

import { contactWhatsAppSectionPath } from "../contacts/contactDeepLinks";
import type {
  CustomerFilmOutcomeWeek,
  CustomerFilmOutcomes,
  EchoIncident,
  EchoIncidentSummary,
  IncidentListState,
} from "./types";

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline";

/** The Incidents tab's route (More → Operations). */
export const INCIDENTS_PATH = "/echo-incidents";

// --- API paths -------------------------------------------------------------

/** The page's time window in days: the last 24 hours by default. */
export const HISTORY_WINDOWS = [1, 7, 30] as const;
export type HistoryWindow = (typeof HISTORY_WINDOWS)[number];
export const DEFAULT_HISTORY_WINDOW: HistoryWindow = 1;
/** Per-browser memory of the chosen window (a view preference, like the Director View's). */
export const INCIDENTS_WINDOW_KEY = "echoflicks.incidents.window";
/** Open incidents turn unrecovered after at most 14 days (policy bound), so 30 days covers every open one. */
export const OPEN_LOOKBACK_DAYS = 30;
/** Weeks of customer 72-hour outcomes on the weekly card. */
export const OUTCOME_WEEKS = 8;
/** The policy's default; the summary's `policy` block overrides it. */
export const DEFAULT_OVERDUE_MINUTES = 60;
export const DEFAULT_UNRECOVERED_HOURS = 72;

const clampInt = (value: number, min: number, max: number) =>
  Math.min(
    max,
    Math.max(min, Math.round(Number.isFinite(value) ? value : min)),
  );

export function incidentListPath(
  state: IncidentListState,
  days: number,
): string {
  const query = new URLSearchParams({
    state,
    days: String(clampInt(days, 1, 90)),
  });
  return `/monitoring/echo-incidents?${query.toString()}`;
}

export function incidentSummaryPath(days: number, weeks: number): string {
  const query = new URLSearchParams({
    days: String(clampInt(days, 1, 90)),
    weeks: String(clampInt(weeks, 1, 26)),
  });
  return `/monitoring/echo-incidents/summary?${query.toString()}`;
}

// --- Time window -------------------------------------------------------------

/** "24 h" for one day, else "7 days". */
export function windowLabel(days: number): string {
  return days === 1 ? "24 h" : `${days} days`;
}

/** "the last 24 hours" / "the last 7 days". */
export function windowPhrase(days: number): string {
  return days === 1 ? "the last 24 hours" : `the last ${days} days`;
}

export function parseHistoryWindow(value: unknown): HistoryWindow {
  const days = Number(value);
  return (HISTORY_WINDOWS as readonly number[]).includes(days)
    ? (days as HistoryWindow)
    : DEFAULT_HISTORY_WINDOW;
}

type WindowStorage = Pick<Storage, "getItem" | "setItem">;

const browserStorage = (): WindowStorage | null =>
  typeof window === "undefined" ? null : window.localStorage;

/** The window this browser last chose; the default when storage is empty or blocked. */
export function readStoredWindow(
  storage: () => WindowStorage | null = browserStorage,
): HistoryWindow {
  try {
    return parseHistoryWindow(storage()?.getItem(INCIDENTS_WINDOW_KEY));
  } catch {
    return DEFAULT_HISTORY_WINDOW;
  }
}

export function storeWindow(
  days: HistoryWindow,
  storage: () => WindowStorage | null = browserStorage,
): void {
  try {
    storage()?.setItem(INCIDENTS_WINDOW_KEY, String(days));
  } catch {
    // A blocked or full storage only loses the preference.
  }
}

/** When an incident began: it opened, else (older rows) its approval. */
export function openedSince(incident: EchoIncident): string | null {
  return incident.opened_at || incident.approved_at || null;
}

/**
 * Open incidents that opened inside the window, and the older ones that are
 * still open, so the page can count and offer them instead of hiding them.
 */
export function splitOpenByWindow(
  incidents: readonly EchoIncident[],
  days: number,
  now: number,
): { inWindow: EchoIncident[]; older: EchoIncident[] } {
  const since = now - days * 24 * 60 * 60_000;
  const inWindow: EchoIncident[] = [];
  const older: EchoIncident[] = [];
  for (const incident of incidents) {
    const opened = parseTime(openedSince(incident));
    (opened != null && opened < since ? older : inWindow).push(incident);
  }
  return { inWindow, older };
}

// --- Errors ------------------------------------------------------------------

export type IncidentsErrorKind = "disabled" | "forbidden" | "error";

/**
 * 503 (`incidents_disabled` / `incidents_unavailable`) means the runtime
 * policy keeps the feature closed in this environment; 401/403 means the
 * operator is not an administrator. Duck-typed on `status` so this module does
 * not import the dashboard client.
 */
export function incidentsErrorKind(error: unknown): IncidentsErrorKind {
  const status =
    error && typeof error === "object" && "status" in error
      ? Number((error as { status: unknown }).status)
      : NaN;
  if (status === 503) return "disabled";
  if (status === 401 || status === 403) return "forbidden";
  return "error";
}

/** Retry transient failures twice; a disabled feature or a denial never recovers by retrying. */
export function shouldRetryIncidents(
  failureCount: number,
  error: unknown,
): boolean {
  return incidentsErrorKind(error) === "error" && failureCount < 2;
}

// --- Labels ------------------------------------------------------------------

const CAUSE_LABELS: Record<string, string> = {
  generation_failed: "Generation failed",
  interview_failed: "Interview failed",
  delivery_failed: "Delivery failed",
  film_rejected: "Rejected by WhatsApp",
  overdue: "Overdue",
  edit_failed: "Edit failed",
};

const CAUSE_VARIANTS: Record<string, BadgeVariant> = {
  generation_failed: "destructive",
  interview_failed: "destructive",
  delivery_failed: "destructive",
  film_rejected: "destructive",
  overdue: "secondary",
  edit_failed: "destructive",
};

/** Stable display order for the cause breakdown. */
export const CAUSE_ORDER = [
  "generation_failed",
  "overdue",
  "film_rejected",
  "delivery_failed",
  "interview_failed",
  "edit_failed",
] as const;

const humanize = (value: string) => {
  const text = value.replaceAll("_", " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
};

export function causeLabel(cause: string | null | undefined): string {
  if (!cause) return "Unknown cause";
  return CAUSE_LABELS[cause] ?? humanize(cause);
}

export function causeVariant(cause: string | null | undefined): BadgeVariant {
  return (cause && CAUSE_VARIANTS[cause]) || "outline";
}

/** A failed edit of a Studio film (an agency's re-cut) rather than a WhatsApp edit. */
export function isStudioRecut(
  incident: Pick<EchoIncident, "user_id" | "cause">,
): boolean {
  return incident.cause === "edit_failed" && isStudioIncident(incident);
}

/**
 * A failed edit asked on my.echoflicks.com (bot #546 publishes `channel: "web"`), not in
 * WhatsApp or the Studio gallery (WRP-045).
 */
export function isWebEdit(
  incident: Pick<EchoIncident, "user_id" | "cause" | "edit_facts">,
): boolean {
  return (
    incident.cause === "edit_failed" &&
    !isStudioIncident(incident) &&
    incident.edit_facts?.channel === "web"
  );
}

/** The cause as the row shows it: a Studio re-cut and a web edit are named as such. */
export function incidentCauseLabel(incident: EchoIncident): string {
  if (isStudioRecut(incident)) return "Studio re-cut failed";
  if (isWebEdit(incident)) return "Web edit failed";
  return causeLabel(incident.cause);
}

export function incidentCauseDescription(
  incident: EchoIncident,
  overdueMinutes: number = DEFAULT_OVERDUE_MINUTES,
): string {
  if (isWebEdit(incident)) {
    return "An edit asked on my.echoflicks.com failed. The customer kept the film.";
  }
  if (!isStudioRecut(incident)) {
    return causeDescription(incident.cause, overdueMinutes);
  }
  const saw = incident.edit_facts?.customer_message;
  const kept =
    "A re-cut of a delivered Studio film failed. The agency kept the film.";
  return saw ? `${kept} The agency saw: ${saw}` : kept;
}

/** The overdue cause spelled out with the policy's threshold, for tooltips. */
export function causeDescription(
  cause: string | null | undefined,
  overdueMinutes: number = DEFAULT_OVERDUE_MINUTES,
): string {
  switch (cause) {
    case "generation_failed":
      return "The render failed after the customer approved it.";
    case "interview_failed":
      return "The Echo failed before the customer approved it.";
    case "delivery_failed":
      return "The film was made but could not be sent.";
    case "film_rejected":
      return "WhatsApp rejected the film message, so the customer does not have it.";
    case "overdue":
      return `No film ${overdueMinutes} minutes after approval.`;
    case "edit_failed":
      return "An edit of a delivered film failed. The customer kept the film and was offered recovery.";
    default:
      return causeLabel(cause);
  }
}

const STATE_LABELS: Record<string, string> = {
  open: "Open",
  resolved: "Recovered",
  unrecovered: "Unrecovered",
  watching: "Watching",
  delivered: "Delivered",
};

const STATE_VARIANTS: Record<string, BadgeVariant> = {
  open: "destructive",
  resolved: "default",
  unrecovered: "destructive",
  watching: "outline",
  delivered: "secondary",
};

export function stateLabel(state: string | null | undefined): string {
  if (!state) return "Unknown";
  return STATE_LABELS[state] ?? humanize(state);
}

export function stateVariant(state: string | null | undefined): BadgeVariant {
  return (state && STATE_VARIANTS[state]) || "outline";
}

export function resolutionLabel(resolution: string | null | undefined): string {
  if (resolution === "delivered_same_echo") return "Same Echo";
  if (resolution === "delivered_later_echo") return "Later Echo";
  if (resolution === "delivered_remake") return "CRM remake";
  if (!resolution) return "—";
  return humanize(resolution);
}

/** `stg` is customer-facing production; `staging` is engineering staging. */
export function environmentLabel(environment: string | null | undefined) {
  if (environment === "stg") return "Production (stg)";
  if (environment === "staging") return "Engineering staging (staging)";
  return environment ? `Environment ${environment}` : null;
}

export function explanationLine(
  overdueMinutes: number = DEFAULT_OVERDUE_MINUTES,
): string {
  return `An incident opens when a customer approved their film and it failed, was rejected by WhatsApp, or has not arrived ${overdueMinutes} minutes later. It resolves when the customer gets a film — from the same Echo, a CRM remake or a later Echo — and the Recovery column shows any re-drive or retry under way.`;
}

// --- Durations and times -------------------------------------------------------

/** 45 → "45m", 135 → "2h 15m", 1560 → "1d 2h"; under a minute → "<1m". */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return "—";
  const total = Math.floor(minutes);
  if (total < 1) return "<1m";
  if (total < 60) return `${total}m`;
  if (total < 24 * 60) {
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    return rest ? `${hours}h ${rest}m` : `${hours}h`;
  }
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}

const parseTime = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export function minutesBetween(
  from: string | null | undefined,
  to: string | null | undefined,
): number | null {
  const start = parseTime(from);
  const end = parseTime(to);
  if (start == null || end == null) return null;
  return Math.max(0, (end - start) / 60_000);
}

/** How long ago `since` was, e.g. "2h 15m"; a timestamp slightly in the future (clock skew) reads "<1m". */
export function formatAge(
  since: string | null | undefined,
  now: number,
): string {
  const start = parseTime(since);
  if (start == null) return "—";
  return formatMinutes(Math.max(0, (now - start) / 60_000));
}

/** Local date and time of an ISO timestamp, "—" when absent or unparsable. */
export function formatTimestamp(value: string | null | undefined): string {
  const parsed = parseTime(value);
  if (parsed == null) return "—";
  return new Date(parsed).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The time a customer has been waiting: from approval, else from the incident opening. */
export function waitingSince(incident: EchoIncident): string | null {
  return incident.approved_at || incident.opened_at || null;
}

/** Time to film for a resolved incident: the API's figure, else approval → resolution. */
export function timeToFilmMinutes(incident: EchoIncident): number | null {
  if (incident.minutes_to_film != null) return incident.minutes_to_film;
  if (incident.state !== "resolved") return null;
  return minutesBetween(
    incident.approved_at ?? incident.opened_at,
    incident.film_sent_at ?? incident.resolved_at,
  );
}

// --- Customer identity -------------------------------------------------------

/** The last four digits of a WhatsApp id, e.g. "…5434". Never the full number. */
export function maskPhone(userId: string | null | undefined): string {
  const digits = (userId ?? "").replace(/\D/g, "");
  if (!digits) return "—";
  return `…${digits.slice(-4)}`;
}

/**
 * A Studio (agency portal) film runs under a phantom `studio-<echo>` user, not
 * a phone number; its customer is the agency waiting on that listing.
 */
export function isStudioIncident(incident: Pick<EchoIncident, "user_id">) {
  return (incident.user_id ?? "").startsWith("studio-");
}

export function customerName(incident: EchoIncident): string {
  if (isStudioIncident(incident)) {
    return incident.edit_facts?.agency_name?.trim() || "Studio film";
  }
  const name = incident.contact_name?.trim();
  return name || "Unknown customer";
}

/** The line under the name: the masked phone, or a Studio film's listing. */
export function customerDetail(incident: EchoIncident): string {
  if (!isStudioIncident(incident)) return maskPhone(incident.user_id);
  const property = incident.edit_facts?.property_name?.trim();
  if (property) return property;
  return incident.listing_group
    ? `Listing ${shortWorkloadId(incident.listing_group)}`
    : "Agency listing";
}

export function shortWorkloadId(workloadId: string | null | undefined) {
  return (workloadId ?? "").slice(0, 8) || "—";
}

export function echoLabel(incident: EchoIncident): string {
  const title = incident.title?.trim();
  return title || "Untitled Echo";
}

export function occasionLabel(occasion: string | null | undefined) {
  return occasion?.trim() ? humanize(occasion.trim()) : null;
}

// --- Customer notices and the WhatsApp window ----------------------------------

export type NoticeState = {
  kind: "failure" | "delay" | "none";
  label: string;
  at: string | null;
};

/** Whether the customer was told: a failure notice outranks a "taking longer" notice. */
export function customerNotice(incident: EchoIncident): NoticeState {
  if (incident.customer_notified_at)
    return {
      kind: "failure",
      label: "Told it failed",
      at: incident.customer_notified_at,
    };
  if (incident.delay_notified_at)
    return {
      kind: "delay",
      label: "Told it's taking longer",
      at: incident.delay_notified_at,
    };
  return { kind: "none", label: "Not told", at: null };
}

export type WindowState =
  | { kind: "open"; until: string; remainingMinutes: number; label: string }
  | { kind: "closed"; until: string; label: string }
  | { kind: "unknown"; label: string };

/**
 * The customer's 24-hour WhatsApp window. While it is open a free-form
 * message reaches them; once it closes only an approved template does.
 */
export function windowState(
  expiresAt: string | null | undefined,
  now: number,
): WindowState {
  const until = parseTime(expiresAt);
  if (until == null || !expiresAt) return { kind: "unknown", label: "Unknown" };
  if (until > now) {
    const remainingMinutes = (until - now) / 60_000;
    return {
      kind: "open",
      until: expiresAt,
      remainingMinutes,
      label: `Open · ${formatMinutes(remainingMinutes)} left`,
    };
  }
  return {
    kind: "closed",
    until: expiresAt,
    label: "Closed · template needed",
  };
}

// --- Links -------------------------------------------------------------------

export type IncidentLinks = {
  echo: string;
  studio: string;
  chat: string | null;
  recovery: string | null;
  /** The newer Echo trying to recover this one. */
  retry: string | null;
  /** A failed edit: the delivered film it was an edit of. */
  original: string | null;
};

export function echoPath(workloadId: string) {
  return `/echoflicks_echoes/${encodeURIComponent(workloadId)}/show`;
}

const otherEcho = (incident: EchoIncident, workloadId?: string | null) =>
  workloadId && workloadId !== incident.workload_id
    ? echoPath(workloadId)
    : null;

export function incidentLinks(incident: EchoIncident): IncidentLinks {
  return {
    echo: echoPath(incident.workload_id),
    studio: `/echo-studio/${encodeURIComponent(incident.workload_id)}`,
    // A Studio film has no WhatsApp chat: its customer is an agency in the portal.
    chat:
      incident.user_id && !isStudioIncident(incident)
        ? contactWhatsAppSectionPath(incident.user_id)
        : null,
    // A remake renders under the re-made Echo's page, so it has no page of its own.
    recovery:
      incident.resolution === "delivered_remake"
        ? null
        : otherEcho(incident, incident.recovery_workload_id),
    retry:
      incident.retry_kind === "new_echo"
        ? otherEcho(incident, incident.retry_workload_id)
        : null,
    original: otherEcho(incident, incident.parent_workload_id),
  };
}

// --- Recovery attempts and live status ---------------------------------------

const PIPELINE_STATUS_LABELS: Record<string, string> = {
  waiting_for_user: "waiting for the customer",
  waiting_for_photos: "waiting for photos",
  active: "in progress",
  generating: "generating",
  rendering: "rendering",
  delivery_pending: "delivering the film",
  delivery_queued: "delivering the film",
  delivery_submitted: "delivering the film",
  completed: "film made",
  delivered: "film delivered",
  failed: "failed",
  cancelled: "cancelled",
};

/** Statuses of an Echo that is still being worked on. */
const STILL_RUNNING = new Set([
  "active",
  "generating",
  "rendering",
  "delivery_pending",
  "delivery_queued",
  "delivery_submitted",
]);

/** A session's pipeline status in words, e.g. "waiting for the customer". */
export function pipelineStatusLabel(status: string | null | undefined) {
  if (!status) return null;
  return PIPELINE_STATUS_LABELS[status] ?? humanize(status).toLowerCase();
}

const RETRY_SOURCE_LABELS: Record<string, string> = {
  "operator.finalization_resume.accepted": "Operator resumed the finalization",
  "composition.queued": "Film re-queued for rendering",
  "generation.requested": "Echo re-initiated",
  "recovery.delivery.requested": "Customer asked for the film again",
  "echo.started": "Customer has a newer Echo",
  "studio.echo.admitted": "Listing re-made as a new film",
};

export function retrySourceLabel(source: string | null | undefined) {
  if (!source) return "Recovery started";
  return RETRY_SOURCE_LABELS[source] ?? humanize(source.replaceAll(".", " "));
}

export type RecoveryView = {
  kind: "redrive" | "new_echo" | "none";
  /** What is being done, e.g. "Re-drive · Operator resumed the finalization". */
  label: string;
  /** Where it is now, e.g. "rendering · 4m ago". */
  status: string | null;
  tone: "progress" | "failed" | "idle";
  /** When the attempt started. */
  at: string | null;
};

const liveStatus = (
  status: string | null | undefined,
  at: string | null | undefined,
  now: number,
) => {
  const label = pipelineStatusLabel(status);
  if (!label) return null;
  return parseTime(at) != null ? `${label} · ${formatAge(at, now)} ago` : label;
};

/**
 * What is being done about an open incident and where that stands right now:
 * an operator re-drive of the same Echo, a newer Echo of the customer (or a
 * re-make of the Studio listing), or nothing yet.
 */
export function recoveryView(
  incident: EchoIncident,
  now: number,
): RecoveryView {
  if (incident.retry_kind === "same_echo") {
    return {
      kind: "redrive",
      label: `Re-drive · ${retrySourceLabel(incident.retry_source)}`,
      status: liveStatus(incident.echo_status, incident.echo_status_at, now),
      tone: incident.echo_status === "failed" ? "failed" : "progress",
      at: incident.retry_at ?? null,
    };
  }
  if (incident.retry_kind === "new_echo" && incident.retry_workload_id) {
    const id = shortWorkloadId(incident.retry_workload_id);
    const retryStatus = liveStatus(
      incident.retry_status,
      incident.retry_status_at,
      now,
    );
    // The stuck Echo may still be running too (an operator re-drive the
    // customer did not wait for): say so instead of hiding it.
    const ownStatus = STILL_RUNNING.has(incident.echo_status ?? "")
      ? `this Echo ${liveStatus(incident.echo_status, incident.echo_status_at, now)}`
      : null;
    return {
      kind: "new_echo",
      label: isStudioIncident(incident)
        ? `Re-made as film ${id}`
        : `${retrySourceLabel(incident.retry_source)} · ${id}`,
      status: [retryStatus, ownStatus].filter(Boolean).join(" · ") || null,
      tone: incident.retry_status === "failed" ? "failed" : "progress",
      at: incident.retry_at ?? null,
    };
  }
  const status = liveStatus(incident.echo_status, incident.echo_status_at, now);
  return {
    kind: "none",
    label: "No recovery yet",
    status: status ? `Echo ${status}` : null,
    tone: "idle",
    at: null,
  };
}

// --- Sorting and filtering -----------------------------------------------------

const timeOr = (value: string | null | undefined, fallback: number) =>
  parseTime(value) ?? fallback;

/** Open incidents: the customer who has waited longest first. */
export function sortOpenIncidents(
  incidents: readonly EchoIncident[],
): EchoIncident[] {
  return incidents
    .filter((incident) => incident.state === "open")
    .sort(
      (a, b) =>
        timeOr(waitingSince(a), Infinity) - timeOr(waitingSince(b), Infinity) ||
        a.workload_id.localeCompare(b.workload_id),
    );
}

const historyTime = (incident: EchoIncident) =>
  timeOr(
    incident.resolved_at ?? incident.opened_at ?? incident.approved_at,
    -Infinity,
  );

/** Resolved and unrecovered incidents, most recent outcome first. */
export function sortHistory(
  incidents: readonly EchoIncident[],
): EchoIncident[] {
  return incidents
    .filter(
      (incident) =>
        incident.state === "resolved" || incident.state === "unrecovered",
    )
    .sort(
      (a, b) =>
        historyTime(b) - historyTime(a) ||
        a.workload_id.localeCompare(b.workload_id),
    );
}

// --- Tiles and causes ----------------------------------------------------------

export type CauseCount = {
  cause: string;
  label: string;
  count: number;
  variant: BadgeVariant;
};

/** Non-zero causes, largest first, ties in the fixed cause order. */
export function causeBreakdown(
  causes: EchoIncidentSummary["causes"] | null | undefined,
): CauseCount[] {
  if (!causes) return [];
  const order = (cause: string) => {
    const index = (CAUSE_ORDER as readonly string[]).indexOf(cause);
    return index === -1 ? CAUSE_ORDER.length : index;
  };
  return Object.entries(causes)
    .filter(
      (entry): entry is [string, number] =>
        typeof entry[1] === "number" && entry[1] > 0,
    )
    .map(([cause, count]) => ({
      cause,
      label: causeLabel(cause),
      count,
      variant: causeVariant(cause),
    }))
    .sort(
      (a, b) =>
        b.count - a.count ||
        order(a.cause) - order(b.cause) ||
        a.cause.localeCompare(b.cause),
    );
}

// --- Customer 72-hour outcomes -------------------------------------------------

const isCount = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/** Customers whose 72 hours have elapsed: approved less pending. */
export function decidedCustomers(outcomes: CustomerFilmOutcomes): number {
  return Math.max(0, outcomes.customers_approved - outcomes.customers_pending);
}

/**
 * The share of decided customers without a film within 72 hours. The API's
 * `rate_pct` wins; when it is absent the same formula is applied here
 * (without / (approved − pending) × 100, one decimal, null when nothing is
 * decided yet).
 */
export function customerFilmRate(
  outcomes: CustomerFilmOutcomes,
): number | null {
  if (isCount(outcomes.rate_pct)) return outcomes.rate_pct;
  const decided = decidedCustomers(outcomes);
  if (decided <= 0) return null;
  return (
    Math.round((1000 * outcomes.customers_without_film_72h) / decided) / 10
  );
}

export function formatRate(rate: number | null | undefined): string {
  return rate == null || !Number.isFinite(rate) ? "—" : `${rate}%`;
}

/** Accepts the block only when every count is present, as older backends omit it entirely. */
export function parseCustomerFilmOutcomes(
  value: unknown,
): CustomerFilmOutcomes | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  if (
    !isCount(record.customers_approved) ||
    !isCount(record.customers_with_film_within_72h) ||
    !isCount(record.customers_without_film_72h) ||
    !isCount(record.customers_pending)
  )
    return null;
  return {
    customers_approved: record.customers_approved,
    customers_with_film_within_72h: record.customers_with_film_within_72h,
    customers_without_film_72h: record.customers_without_film_72h,
    customers_pending: record.customers_pending,
    rate_pct: isCount(record.rate_pct) ? record.rate_pct : null,
  };
}

export type CustomerFilmTile = {
  value: string;
  sublabel: string;
  muted: boolean;
};

/** The Funnel tab's KPI: "4 / 116" with the rate and the pending count; "—" on older backends. */
export function customerFilmTile(value: unknown): CustomerFilmTile {
  const outcomes = parseCustomerFilmOutcomes(value);
  if (!outcomes)
    return { value: "—", sublabel: "not reported yet", muted: true };
  const decided = decidedCustomers(outcomes);
  const rate = customerFilmRate(outcomes);
  const pending = outcomes.customers_pending.toLocaleString("en-US");
  return {
    value: `${outcomes.customers_without_film_72h.toLocaleString("en-US")} / ${decided.toLocaleString("en-US")}`,
    sublabel: `${formatRate(rate)} of decided · ${pending} pending`,
    muted: false,
  };
}

export type OutcomeWeekRow = {
  key: string;
  week: string;
  weekStart: string;
  approved: number;
  withFilm: number;
  without: number;
  pending: number;
  decided: number;
  rate: string;
  /** True when some customers are still inside their 72 hours. */
  incomplete: boolean;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** `2026-09-14` → "14 Sep"; anything else is shown as given. */
export function formatWeekStart(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? "");
  if (!match) return value ?? "";
  const month = MONTHS[Number(match[2]) - 1];
  return month ? `${Number(match[3])} ${month}` : (value ?? "");
}

function outcomeRow(
  key: string,
  week: string,
  weekStart: string,
  outcomes: CustomerFilmOutcomes,
): OutcomeWeekRow {
  return {
    key,
    week,
    weekStart,
    approved: outcomes.customers_approved,
    withFilm: outcomes.customers_with_film_within_72h,
    without: outcomes.customers_without_film_72h,
    pending: outcomes.customers_pending,
    decided: decidedCustomers(outcomes),
    rate: formatRate(customerFilmRate(outcomes)),
    incomplete: outcomes.customers_pending > 0,
  };
}

/** Weekly rows, newest week first. */
export function outcomeWeekRows(
  weeks: readonly CustomerFilmOutcomeWeek[] | null | undefined,
): OutcomeWeekRow[] {
  return [...(weeks ?? [])]
    .sort(
      (a, b) =>
        (b.week_start ?? "").localeCompare(a.week_start ?? "") ||
        (b.week ?? "").localeCompare(a.week ?? ""),
    )
    .map((week) =>
      outcomeRow(week.week, week.week, formatWeekStart(week.week_start), week),
    );
}

export function outcomeTotalsRow(
  totals: CustomerFilmOutcomes | null | undefined,
): OutcomeWeekRow | null {
  const parsed = parseCustomerFilmOutcomes(totals);
  return parsed ? outcomeRow("total", "Total", "", parsed) : null;
}
